import { computed, ref } from "vue";
import { RouteLocationRaw } from "vue-router";

import * as Sentry from "@sentry/vue";
import { defineStore, storeToRefs } from "pinia";
import { useApp } from "../../composition-functions/app";
import { useCommon } from "../../composition-functions/common";
import { updateCsrfToken } from "../../composition-functions/csrfToken";
import { Language } from "../../composition-functions/language/Language";
import {
  IDeleteUserParams,
  IGetGlobalUserParams,
  IGetGlobalUsersParams,
  IGetUsersParams,
  IRedirectUserToHomeParams,
  ISaveGlobalUserParams,
  ISaveUserParams,
  ISaveUserProfileParams,
  ISendUserCredentialsParams,
  ISetAuthenticationStateParams,
  IUnblockUserParams,
} from "../../composition-functions/user/IParams";
import {
  GlobalUserDetailDto,
  GlobalUserListDto,
  UserBaseDto,
  UserDetailDto,
  UserEnableTwoFactorDto,
  UserListDto,
  UserProfileDto,
} from "../../composition-functions/user/UserDto";
import { UserType } from "../../composition-functions/user/UserType";
import { ApplicationTheme } from "../../enums/ApplicationTheme";
import { UserPermission } from "../../enums/UserPermission";
import { changeI18nLanguage, getGlobalTranslation } from "../../i18n";
import { IQueryListResult } from "../../interfaces/IResponses";
import { MandantTextsDto } from "../../mandantTexts/dto.mandantTexts";
import router from "../../router";
import mapLanguageToApplicationLanguageIsoCode from "../../util/mapLanguageToLanguageIsoCode";

async function enableTwoFactor(): Promise<UserEnableTwoFactorDto> {
  const { postRequest } = useCommon();

  const response = await postRequest({ path: "/admin/user/enableTwoFactor" });
  const result = await response.json();
  return result;
}

async function getGlobalUser({ userId }: IGetGlobalUserParams): Promise<GlobalUserDetailDto> {
  const { getRequest } = useCommon();

  const response = await getRequest({ path: `/admin/user/global/${userId}` });
  const result = await response.json();
  return result;
}

async function getGlobalUsers({
  currentPage,
  pageSize,
  searchTerm,
}: IGetGlobalUsersParams): Promise<IQueryListResult<GlobalUserListDto>> {
  const { getRequest } = useCommon();

  const urlSearchParams = new URLSearchParams({
    searchTerm,
    currentPage: currentPage.toString(),
    pageSize: pageSize.toString(),
  });

  const response = await getRequest({ path: "/admin/user/global", urlSearchParams });
  const result = await response.json();
  return result;
}

async function getUser(userId: number): Promise<UserDetailDto> {
  const { getRequest } = useCommon();

  const response = await getRequest({ path: `/admin/user/${userId}` });
  const result = await response.json();
  return result;
}

export const unknownUser: GlobalUserListDto = {
  id: 0,
  emailAddress: getGlobalTranslation({ message: "message.noUser" }),
  language: Language.GERMAN,
  applicationTheme: ApplicationTheme.DARK,
};

async function getGlobalUserAndUserFilterOptions(searchTerm: null | string) {
  const usersForFilterOptions = await getUsers({
    currentPage: 0,
    pageSize: 10,
    searchTerm: searchTerm ?? "",
    mandants: [],
    collectiveGroups: [],
  });

  const globalUsersForFilterOptions = await getGlobalUsers({
    currentPage: 0,
    pageSize: 10,
    searchTerm: searchTerm ?? "",
  });

  const globaUsersAndUsersForFilterOptions = [...usersForFilterOptions.items, ...globalUsersForFilterOptions.items];

  if (searchTerm === null || unknownUser.emailAddress.toLowerCase().includes(searchTerm.toLowerCase())) {
    globaUsersAndUsersForFilterOptions.unshift(unknownUser);
  }

  return globaUsersAndUsersForFilterOptions.map((user) => {
    return {
      value: user.id,
      label: `${user.id} - ${user.emailAddress}`,
    };
  });
}

async function getUsersByIds(userIds: number[]): Promise<UserBaseDto[]> {
  const { getRequest } = useCommon();

  const urlSearchParams = new URLSearchParams({
    userIds: userIds.join(","),
  });

  const response = await getRequest({ path: "/admin/user/getByIds", urlSearchParams });
  const result = await response.json();
  return result;
}

async function getUsers({
  currentPage,
  pageSize,
  searchTerm,
  mandants,
  collectiveGroups,
}: IGetUsersParams): Promise<IQueryListResult<UserListDto>> {
  const { getRequest } = useCommon();

  const urlSearchParams = new URLSearchParams({
    searchTerm,
    currentPage: currentPage.toString(),
    pageSize: pageSize.toString(),
    mandants: mandants.join(","),
    collectiveGroups: collectiveGroups.join(","),
  });

  const response = await getRequest({ path: "/admin/user", urlSearchParams });
  const result = await response.json();
  return result;
}

async function saveUser({ user }: ISaveUserParams): Promise<{ id: number }> {
  const { postRequest } = useCommon();

  const response = await postRequest({ path: "/admin/user", data: JSON.stringify(user) });
  const result = await response.json();
  return result;
}

async function deleteUser({ userId }: IDeleteUserParams) {
  const { deleteRequest } = useCommon();
  await deleteRequest({ path: `/admin/user/${userId}` });
}

async function getUserProfile(): Promise<UserProfileDto> {
  const { getRequest } = useCommon();

  const response = await getRequest({ path: "/admin/user/profile" });
  const result = await response.json();
  return result;
}

async function saveGlobalUser({ user }: ISaveGlobalUserParams): Promise<{ id: number }> {
  const { postRequest } = useCommon();

  const response = await postRequest({ path: "/admin/user/global", data: JSON.stringify(user) });
  const result = await response.json();
  return result;
}

async function sendUserCredentials({ userId }: ISendUserCredentialsParams) {
  const { postRequest } = useCommon();
  await postRequest({ path: `/admin/user/${userId}/sendCredentials` });
}

async function unblockUser({ userId }: IUnblockUserParams) {
  const { postRequest } = useCommon();
  await postRequest({ path: `/admin/user/${userId}/unblockUser` });
}

async function ping() {
  const { setInitialPingDone } = useApp();
  const { getRequest } = useCommon();
  const userStore = useUser();
  const { resetAuthenticationState, setAuthenticationState } = userStore;

  try {
    const response = await getRequest({ path: "/admin/user/ping" });
    const result = await response.json();
    updateCsrfToken(result);

    setAuthenticationState({
      authenticationState: true,
      userId: result.userId,
      userLanguage: result.userLanguage,
      userType: result.userType,
      mandantIds: result.mandantIds,
      globalMandantTexts: result.globalMandantTexts,
      applicationTheme: result.applicationTheme,
      isTwoFactorEnabled: result.isTwoFactorEnabled,
      permissions: result.permissions,
      canAccessSensitiveMandantData: result.canAccessSensitiveMandantData,
    });
    setInitialPingDone({ initialPingState: true });
  } catch (error) {
    resetAuthenticationState();
    setInitialPingDone({ initialPingState: true });
    throw error;
  }
}

async function redirectUserToHome(options?: IRedirectUserToHomeParams) {
  const { setInitialPingDone } = useApp();
  const userStore = useUser();
  const { userType, mandantIds } = storeToRefs(userStore);
  const { resetAuthenticationState } = userStore;

  const replaceParams: RouteLocationRaw = {};

  if (options?.query !== undefined) {
    replaceParams.query = options.query;
  }

  if (userType.value !== UserType.NONE) {
    switch (userType.value) {
      case UserType.SUPER_ADMIN:
      case UserType.ADMIN:
        replaceParams.name = "adminDashboard";
        router.replace(replaceParams);
        break;

      case UserType.NORMAL_MULTIPLE:
        replaceParams.name = "mandantsList";
        router.replace(replaceParams);
        break;

      case UserType.NORMAL_SINGLE:
        replaceParams.name = "mandantDashboard";
        replaceParams.params = { mandantId: `${mandantIds.value[0]}` };
        router.replace(replaceParams);
        break;

      default:
        resetAuthenticationState();
        setInitialPingDone({ initialPingState: true });

        if (router.currentRoute.value.name !== "login") {
          router.push({ name: "login" });
        }
        break;
    }
  }
}

async function resetPassword({ email }: { email: string }) {
  const { postRequest } = useCommon();
  await postRequest({ path: "/admin/user/resetPassword", data: JSON.stringify({ email }) });
}

async function saveUserProfile({ user }: ISaveUserProfileParams) {
  const { postRequest } = useCommon();
  await postRequest({ path: "/admin/user/profile/set", data: JSON.stringify(user) });
}

async function verifyTwoFactor({ verificationCode }: { verificationCode: string }) {
  const { postRequest } = useCommon();
  await postRequest({ path: "/admin/user/verifyTwoFactor", data: JSON.stringify({ verificationCode }) });
}

async function disableTwoFactor({ userId }: { userId: number }) {
  const { postRequest } = useCommon();
  await postRequest({ path: `/admin/user/${userId}/disableTwoFactor` });
}

async function verifyTwoFactorLogin({ verificationCode }: { verificationCode: string }) {
  const { postRequest } = useCommon();

  await postRequest({ path: "/admin/user/verifyTwoFactorLogin", data: JSON.stringify({ verificationCode }) });
  await ping();
}

const getBrowserLanguage = (): Language => {
  switch (navigator.language.split("-")[0]) {
    case "it":
      return Language.ITALIAN;
    default:
      return Language.GERMAN;
  }
};

export const useUser = defineStore("userStore", () => {
  const userId = ref<null | number>(null);
  const userLanguage = ref<null | Language>(null);
  const userLanguageIsoCode = computed(() => {
    return mapLanguageToApplicationLanguageIsoCode(userLanguage.value ?? getBrowserLanguage());
  });
  const userType = ref<UserType>(UserType.NONE);
  const isTwoFactorEnabled = ref<boolean>(false);
  const mandantIds = ref<number[]>([]);
  const isAuthenticated = ref<boolean>(false);
  const changePassword = ref<boolean>(false);
  const changePasswordMessage = ref<null | string>(null);
  const globalMandantTexts = ref<undefined | MandantTextsDto>(undefined);
  const applicationTheme = ref<ApplicationTheme>(ApplicationTheme.SYSTEM);
  const permissions = ref<UserPermission[]>([]);
  const canAccessSensitiveMandantData = ref<boolean>(false);

  const setAuthenticationState = (params: ISetAuthenticationStateParams) => {
    isAuthenticated.value = params.authenticationState;
    userId.value = params.userId;
    userLanguage.value = params.userLanguage;
    userType.value = params.userType;
    isTwoFactorEnabled.value = params.isTwoFactorEnabled;
    mandantIds.value = params.mandantIds;
    globalMandantTexts.value = params.globalMandantTexts;
    applicationTheme.value = params.applicationTheme;
    permissions.value = params.permissions;
    canAccessSensitiveMandantData.value = params.canAccessSensitiveMandantData;

    if (userLanguage.value !== null) {
      changeI18nLanguage(userLanguage.value);
    }

    if (userId.value) {
      Sentry.setUser({
        id: userId.value,
      });
    } else {
      Sentry.setUser(null);
    }
  };

  async function setApplicationTheme(selectedApplicationTheme: ApplicationTheme) {
    const { postRequest } = useCommon();
    await postRequest({
      path: "/admin/user/setApplicationTheme",
      data: JSON.stringify({ applicationTheme: selectedApplicationTheme }),
    });
    applicationTheme.value = selectedApplicationTheme;
  }

  type SetPasswordChangeParams = {
    shouldChangePassword: boolean;
    newChangePasswordMessage: null | string;
  };

  async function setPasswordChange({ shouldChangePassword, newChangePasswordMessage }: SetPasswordChangeParams) {
    changePassword.value = shouldChangePassword;
    changePasswordMessage.value = newChangePasswordMessage;
  }

  const resetAuthenticationState = () => {
    isAuthenticated.value = false;
    userId.value = null;
    userLanguage.value = null;
    userType.value = UserType.NONE;
    isTwoFactorEnabled.value = false;
    mandantIds.value = [];
    globalMandantTexts.value = undefined;
    Sentry.setUser(null);
  };

  return {
    userId,
    userLanguage,
    userLanguageIsoCode,
    userType,
    isTwoFactorEnabled,
    mandantIds,
    isAuthenticated,
    changePassword,
    changePasswordMessage,
    globalMandantTexts,
    applicationTheme,
    permissions,
    canAccessSensitiveMandantData,
    enableTwoFactor,
    getUser,
    saveUser,
    deleteUser,
    getUsers,
    getUsersByIds,
    getGlobalUserAndUserFilterOptions,
    getGlobalUser,
    getGlobalUsers,
    saveGlobalUser,
    sendUserCredentials,
    getUserProfile,
    ping,
    redirectUserToHome,
    resetPassword,
    saveUserProfile,
    resetAuthenticationState,
    setAuthenticationState,
    setPasswordChange,
    unblockUser,
    verifyTwoFactor,
    disableTwoFactor,
    verifyTwoFactorLogin,
    getBrowserLanguage,
    setApplicationTheme,
  };
});
